import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const contactSchema = z.object({
  name: z.string()
    .trim()
    .min(2, { message: 'O nome deve ter pelo menos 2 caracteres' })
    .max(100, { message: 'O nome deve ter no máximo 100 caracteres' }),
  email: z.string()
    .trim()
    .email({ message: 'Email inválido' })
    .max(255, { message: 'O email deve ter no máximo 255 caracteres' }),
  phone: z.string()
    .trim()
    .min(9, { message: 'Telefone inválido' })
    .max(20, { message: 'Telefone inválido' }),
  company: z.string()
    .trim()
    .max(100, { message: 'O nome da empresa deve ter no máximo 100 caracteres' })
    .optional(),
  message: z.string()
    .trim()
    .min(10, { message: 'A mensagem deve ter pelo menos 10 caracteres' })
    .max(1000, { message: 'A mensagem deve ter no máximo 1000 caracteres' }),
});

type ContactFormValues = z.infer<typeof contactSchema>;

const ContactSection: React.FC = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: '', email: '', phone: '', company: '', message: '' },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'gtm.init_consent',
          'gtm.uniqueEventId': 1,
        });
      }

      const result = await supabase.functions.invoke('send-contact-email', { body: data });
      if (result.error) throw result.error;

      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }

      toast({
        title: t('contact.success').split('!')[0] + '!',
        description: t('contact.success').split('!')[1],
      });

      form.reset();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: t('contact.error').split('.')[0],
        description: t('contact.error').split('.')[1],
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 md:py-32 bg-background overflow-hidden"
      aria-labelledby="contact-heading"
    >
      {/* Atmospheric backdrop */}
      <div className="pointer-events-none absolute inset-0 mesh-bg opacity-50" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-[600px] bg-gradient-spotlight" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          className="text-center mb-16 md:mb-20 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.8 }}
        >
          <span className="eyebrow">— {t('contact.eyebrow')}</span>
          <h2
            id="contact-heading"
            className="mt-5 font-display text-foreground text-[clamp(2rem,4.6vw,4rem)] leading-[1.04] tracking-tight"
          >
            {t('contact.title')}
          </h2>
          <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-8 items-start">
          {/* Info panel */}
          <motion.aside
            className="col-span-12 lg:col-span-5"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="surface-elevated rounded-2xl p-5 sm:p-8 md:p-10">
              <span className="pill-tag-lime mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
                {t('contact.formSubtitle')}
              </span>

              <h3 className="font-display text-2xl md:text-3xl text-foreground leading-tight mb-8">
                {t('contact.bookMeeting')}
              </h3>

              {/* Contact channels */}
              <ul className="space-y-5 mb-10">
                <li className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-card text-lime">
                    <Mail className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="eyebrow mb-1">Email</div>
                    <a
                      href="mailto:joao@spawncore.com"
                      className="text-foreground hover:text-lime smooth-transition link-underline"
                    >
                      joao@spawncore.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-card text-lime">
                    <MapPin className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="eyebrow mb-1">Studio</div>
                    <p className="text-foreground">Largo do Esteiro 6</p>
                    <p className="text-muted-foreground text-sm">2050-261 Azambuja, Portugal</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-card text-lime">
                    <Clock className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="eyebrow mb-1">SLA</div>
                    <p className="text-foreground">{t('contact.formSubtitle')}</p>
                  </div>
                </li>
              </ul>

              {/* What happens next */}
              <div className="rounded-xl border border-lime/25 bg-lime/[0.05] p-5">
                <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-lime mb-3">
                  / next steps
                </div>
                <ol className="space-y-2.5 text-sm text-foreground/85">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-lime flex-shrink-0" strokeWidth={2} />
                    <span>{t('process.steps.step1.desc')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-lime flex-shrink-0" strokeWidth={2} />
                    <span>{t('process.steps.step2.desc')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-4 w-4 mt-0.5 text-lime flex-shrink-0" strokeWidth={2} />
                    <span>{t('process.steps.step3.desc')}</span>
                  </li>
                </ol>
              </div>
            </div>
          </motion.aside>

          {/* Form */}
          <motion.div
            className="col-span-12 lg:col-span-7"
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-elevated">
              <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />

              <div className="relative p-5 sm:p-8 md:p-10">
                <div className="flex items-baseline justify-between mb-8 pb-6 border-b border-border/60">
                  <h3 className="font-display text-2xl md:text-3xl text-foreground leading-tight">
                    {t('contact.formTitle')}
                  </h3>
                  <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground hidden sm:inline">
                    / form
                  </span>
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder={t('contact.name')}
                                className="h-12 bg-background/80 border-border focus-visible:ring-lime/40 focus-visible:border-lime/40"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder={t('contact.email')}
                                className="h-12 bg-background/80 border-border focus-visible:ring-lime/40 focus-visible:border-lime/40"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="+351 912 345 678"
                                className="h-12 bg-background/80 border-border focus-visible:ring-lime/40 focus-visible:border-lime/40"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder={t('contact.company')}
                                className="h-12 bg-background/80 border-border focus-visible:ring-lime/40 focus-visible:border-lime/40"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              placeholder={t('contact.message')}
                              className="min-h-[160px] bg-background/80 border-border focus-visible:ring-lime/40 focus-visible:border-lime/40 resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="group h-14 px-7 text-base font-medium bg-primary text-primary-foreground hover:bg-primary-light rounded-full shadow-glow border border-primary-light/30 flex-1 sm:flex-none"
                      >
                        {isSubmitting ? (
                          t('contact.sending')
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            {t('contact.send')}
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground sm:ml-2">
                        {t('contact.formSubtitle')}
                      </p>
                    </div>
                  </form>
                </Form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
